import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Select,
  Option,
  FormControl,
  FormLabel,
  Stack,
} from '@mui/joy'
import { ResponsiveTable } from '../../../components/common/ResponsiveTable'
import Grid from '@mui/joy/Grid'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area,
} from 'recharts'
import { TrendUp as TrendingUp, TrendDown as TrendingDown, ArrowRight as TrendingFlat } from '@phosphor-icons/react'
import { useSalesTrends } from '../../../hooks/useSalesReports'

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatShortCurrency = (amount: number) => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}k`
  }
  return `$${amount}`
}

export default function SalesTrends() {
  const [months, setMonths] = useState(12)

  const { data, isLoading, error } = useSalesTrends({ months })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert color="danger">
        Failed to load trends data. Please try again.
      </Alert>
    )
  }

  if (!data) {
    return (
      <Alert color="warning">
        No trend data available.
      </Alert>
    )
  }

  const { monthly_data, trend_summary } = data

  const renderChangeIcon = (change: number) => {
    if (change > 0) return <TrendingUp color="var(--joy-palette-success-500)" size={16} />
    if (change < 0) return <TrendingDown color="var(--joy-palette-danger-500)" size={16} />
    return <TrendingFlat color="var(--joy-palette-neutral-500)" size={16} />
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Typography level="title-lg">
          Sales Trends
        </Typography>
        <FormControl size="sm">
          <FormLabel>Time Period</FormLabel>
          <Select
            value={months}
            onChange={(_, value) => setMonths(value as number)}
            sx={{ minWidth: { sm: 150 } }}
          >
            <Option value={6}>Last 6 Months</Option>
            <Option value={12}>Last 12 Months</Option>
            <Option value={18}>Last 18 Months</Option>
            <Option value={24}>Last 24 Months</Option>
          </Select>
        </FormControl>
      </Stack>

      {/* Trend Summary */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid xs={12} sm={4}>
          <Card variant="soft" color="primary">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Avg Monthly Revenue (3mo)</Typography>
              <Typography level="h3">{formatCurrency(trend_summary.avg_monthly_revenue_3mo)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} sm={4}>
          <Card variant="soft" color="success">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Avg Monthly Deals (3mo)</Typography>
              <Typography level="h3">{trend_summary.avg_monthly_deals_3mo.toFixed(1)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} sm={4}>
          <Card variant="soft" color="warning">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Avg Win Rate (3mo)</Typography>
              <Typography level="h3">{trend_summary.avg_win_rate_3mo.toFixed(1)}%</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revenue Trend Chart */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Revenue Trend (YoY Comparison)
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={monthly_data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month_display" />
              <YAxis yAxisId="left" tickFormatter={formatShortCurrency} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'Win Rate') return [`${value}%`, name]
                  return [formatCurrency(value as number), name]
                }}
              />
              <Legend />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="yoy_comparison.revenue"
                fill="#E3F2FD"
                stroke="#90CAF9"
                name="Last Year Revenue"
                strokeDasharray="5 5"
              />
              <Bar yAxisId="left" dataKey="revenue" fill="#0B6BCB" name="Revenue" />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="win_rate"
                stroke="#06A77D"
                strokeWidth={2}
                name="Win Rate"
                dot
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Deals Trend Chart */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Deals Won vs Lost
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthly_data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month_display" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="deals_won" stackId="deals" fill="#06A77D" name="Won" />
              <Bar dataKey="deals_lost" stackId="deals" fill="#E57373" name="Lost" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Job Revenue Trend */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Job Revenue Trend
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthly_data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month_display" />
              <YAxis tickFormatter={formatShortCurrency} />
              <Tooltip formatter={(value) => formatCurrency(value as number)} />
              <Legend />
              <Line
                type="monotone"
                dataKey="job_revenue"
                stroke="#F9A825"
                strokeWidth={2}
                name="Job Revenue"
                dot
              />
              <Line
                type="monotone"
                dataKey="yoy_comparison.job_revenue"
                stroke="#FFE082"
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Last Year Job Revenue"
                dot
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Monthly Data Table */}
      <Card variant="outlined">
        <CardContent>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Monthly Breakdown with YoY Comparison
          </Typography>
          <ResponsiveTable<typeof monthly_data[number]>
            rows={monthly_data}
            getRowKey={(m) => m.month}
            columns={[
              {
                key: 'month',
                header: 'Month',
                mobileVariant: 'header',
                render: (m) => <Typography level="title-sm">{m.month_display}</Typography>,
              },
              { key: 'opportunities_created', sortValue: (row) => row.opportunities_created, header: 'Opps Created', render: (m) => <Typography level="body-sm">{m.opportunities_created}</Typography> },
              {
                key: 'deals_won',
                header: 'Deals Won',
                render: (m) => (
                  <Chip size="sm" variant="soft" color="success">
                    {m.deals_won}
                  </Chip>
                ),
              },
              {
                key: 'deals_lost',
                sortValue: (row) => row.deals_lost,
                header: 'Deals Lost',
                render: (m) => (
                  <Chip size="sm" variant="soft" color="danger">
                    {m.deals_lost}
                  </Chip>
                ),
              },
              { key: 'revenue', sortValue: (row) => Number(row.revenue), header: 'Revenue', render: (m) => <Typography level="body-sm">{formatCurrency(m.revenue)}</Typography> },
              {
                key: 'revenue_vs_ly',
                header: 'Revenue vs LY',
                render: (m) => (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {renderChangeIcon(m.yoy_comparison.revenue_change)}
                    <Typography
                      level="body-xs"
                      sx={{
                        color:
                          m.yoy_comparison.revenue_change > 0
                            ? 'success.600'
                            : m.yoy_comparison.revenue_change < 0
                            ? 'danger.600'
                            : 'neutral.600',
                      }}
                    >
                      {m.yoy_comparison.revenue_change > 0 ? '+' : ''}
                      {m.yoy_comparison.revenue_change}%
                    </Typography>
                  </Stack>
                ),
              },
              {
                key: 'win_rate',
                sortValue: (row) => row.win_rate,
                header: 'Win Rate',
                render: (m) => (
                  <Chip
                    size="sm"
                    variant="soft"
                    color={m.win_rate >= 50 ? 'success' : m.win_rate >= 25 ? 'warning' : 'neutral'}
                  >
                    {m.win_rate}%
                  </Chip>
                ),
              },
              { key: 'job_revenue', header: 'Job Revenue', render: (m) => <Typography level="body-sm">{formatCurrency(m.job_revenue)}</Typography> },
              {
                key: 'job_revenue_vs_ly',
                header: 'Job Rev vs LY',
                render: (m) => {
                  const change = m.yoy_comparison.job_revenue > 0
                    ? ((m.job_revenue - m.yoy_comparison.job_revenue) / m.yoy_comparison.job_revenue) * 100
                    : 0;
                  return (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {renderChangeIcon(change)}
                      <Typography
                        level="body-xs"
                        sx={{
                          color:
                            m.job_revenue > m.yoy_comparison.job_revenue
                              ? 'success.600'
                              : m.job_revenue < m.yoy_comparison.job_revenue
                              ? 'danger.600'
                              : 'neutral.600',
                        }}
                      >
                        {m.yoy_comparison.job_revenue > 0 ? `${change.toFixed(0)}%` : 'N/A'}
                      </Typography>
                    </Stack>
                  );
                },
              },
            ]}
          />
        </CardContent>
      </Card>
    </Box>
  )
}
